import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const MARKER_MIN_ZOOM = 12;
const PRICE_LEVEL_COLORS = ["#b91c1c", "#dc2626", "#f97316", "#eab308", "#84cc16", "#16a34a"];
const PRICE_LEVEL_LABELS = ["High red", "Red", "Orange", "Yellow", "Light green", "Green"];
const FUEL_TYPE_OPTIONS = [
  { id: "regular", label: "Regular", priceKey: "regulier" },
  { id: "super", label: "Super", priceKey: "super" },
  { id: "diesel", label: "Diesel", priceKey: "diesel" },
];
const PRICE_OVERLAY_SOURCE_ID = "price-overlay-source";
const PRICE_OVERLAY_GLOW_LAYER_ID = "price-overlay-glow";
const PRICE_OVERLAY_CORE_LAYER_ID = "price-overlay-core";
const WEBGL_MARKER_SOURCE_ID = "webgl-marker-source";
const WEBGL_MARKER_BG_LAYER_ID = "webgl-marker-bg";
const WEBGL_MARKER_TEXT_LAYER_ID = "webgl-marker-text";
const WEBGL_MARKER_PILL_IMAGE_PREFIX = "webgl-marker-pill";
let activeMap = null;
let refreshPricesInPlace = null;

function createRasterStyle(tiles, attribution) {
  return {
    version: 8,
    sources: {
      basemap: {
        type: "raster",
        tiles,
        tileSize: 256,
        attribution,
      },
    },
    layers: [
      {
        id: "basemap",
        type: "raster",
        source: "basemap",
      },
    ],
  };
}

const CARTO_VOYAGER_STYLE = createRasterStyle(
  ["https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"],
  "© OpenStreetMap contributors © CARTO"
);

function colorToImageKey(color) {
  return String(color || "na").replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function drawRoundedPillPath(context, x, y, width, height) {
  const radius = height / 2;
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

function createRoundedPillImage({ fill, stroke = null, strokeWidth = 0 }) {
  const width = 116;
  const height = 64;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (stroke && strokeWidth > 0) {
    drawRoundedPillPath(context, 0, 0, width, height);
    context.fillStyle = stroke;
    context.fill();

    const inset = strokeWidth;
    drawRoundedPillPath(context, inset, inset, width - inset * 2, height - inset * 2);
    context.fillStyle = fill;
    context.fill();
  } else {
    drawRoundedPillPath(context, 0, 0, width, height);
    context.fillStyle = fill;
    context.fill();
  }

  return context.getImageData(0, 0, width, height);
}

function addWebglMarkerImages(map) {
  const imageOptions = { pixelRatio: 2 };

  PRICE_LEVEL_COLORS.forEach((color) => {
    const imageId = `${WEBGL_MARKER_PILL_IMAGE_PREFIX}-${colorToImageKey(color)}`;
    if (!map.hasImage(imageId)) {
      map.addImage(imageId, createRoundedPillImage({
        fill: color,
        stroke: "#ffffff",
        strokeWidth: 6,
      }), imageOptions);
    }
  });

}

function normalizePrice(value) {
  if (value == null || value === "") {
    return null;
  }

  const n = Number(String(value).replace(/[^0-9.,-]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function normalizeLabel(value) {
  return String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function pickPrice(prices, keys) {
  if (Array.isArray(prices)) {
    for (const item of prices) {
      const gasType = normalizeLabel(item?.GasType || item?.gasType || item?.type);
      const wanted = keys.some((k) => gasType.includes(normalizeLabel(k)));
      if (!wanted) {
        continue;
      }

      const candidate = normalizePrice(item?.Price ?? item?.price ?? item?.value);
      if (candidate != null) {
        return candidate;
      }
    }
  } else if (prices && typeof prices === "object") {
    for (const key of keys) {
      const candidate = normalizePrice(prices[key]);
      if (candidate != null) {
        return candidate;
      }
    }
  }

  return null;
}

function formatPrice(value) {
  if (value == null) {
    return "N/A";
  }

  // Most feeds use cents/liter (e.g., 156.9). Keep readable either way.
  return value >= 10 ? `${value.toFixed(1)} cents/L` : `${value.toFixed(3)} $/L`;
}

function formatPriceShort(value) {
  if (value == null) {
    return "N/A";
  }

  return value >= 10 ? value.toFixed(1) : value.toFixed(3);
}

function createPriceMarkerElement(priceValue, fuelLabel) {
  const markerEl = document.createElement("div");
  markerEl.className = "price-marker";

  updatePriceMarkerElement(markerEl, priceValue, null, fuelLabel);

  return markerEl;
}

function updatePriceMarkerElement(markerEl, priceValue, color, fuelLabel) {
  markerEl.classList.toggle("price-marker--na", priceValue == null);
  markerEl.textContent = formatPriceShort(priceValue);
  markerEl.title = priceValue == null
    ? `${fuelLabel} price unavailable`
    : `${fuelLabel}: ${formatPrice(priceValue)}`;
  markerEl.style.backgroundColor = color || "";
}

function getPriceScale(regularPrices) {
  if (regularPrices.length === 0) {
    return null;
  }

  return {
    min: Math.min(...regularPrices),
    max: Math.max(...regularPrices),
  };
}

function getPriceLevelIndex(regularPrice, priceScale) {
  if (regularPrice == null || !priceScale) {
    return -1;
  }

  if (priceScale.max === priceScale.min) {
    return Math.floor(PRICE_LEVEL_COLORS.length / 2);
  }

  const normalized = (regularPrice - priceScale.min) / (priceScale.max - priceScale.min);
  const index = Math.floor(normalized * PRICE_LEVEL_COLORS.length);
  return Math.min(PRICE_LEVEL_COLORS.length - 1, Math.max(0, index));
}

function getPriceLevelColor(regularPrice, priceScale) {
  const levelIndex = getPriceLevelIndex(regularPrice, priceScale);
  if (levelIndex < 0) {
    return null;
  }

  // Lower prices map to greener markers, higher prices map to redder markers.
  const reversedIndex = PRICE_LEVEL_COLORS.length - 1 - levelIndex;
  return PRICE_LEVEL_COLORS[reversedIndex];
}

function getPriceLevelLabel(color) {
  const colorIndex = PRICE_LEVEL_COLORS.indexOf(color);
  return colorIndex >= 0 ? PRICE_LEVEL_LABELS[colorIndex] : "Unavailable";
}

function getPriceLevelRank(color) {
  return PRICE_LEVEL_COLORS.indexOf(color);
}

function haversineDistanceKm(from, to) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const startLat = toRadians(from.lat);
  const endLat = toRadians(to.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
}

async function fetchStationsJson(url) {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    // Fallback for true gzip payloads if server changes behavior.
    const raw = await (await fetch(url, { cache: "no-store" })).arrayBuffer();
    if (typeof DecompressionStream !== "undefined") {
      const ds = new DecompressionStream("gzip");
      const stream = new Blob([raw]).stream().pipeThrough(ds);
      const decompressed = await new Response(stream).text();
      return JSON.parse(decompressed);
    }
    throw new Error("Could not parse station JSON payload");
  }
}

async function loadGasStations() {
  const url = "https://regieessencequebec.ca/stations.geojson.gz";
  const mapContainer = document.getElementById("map");
  const datasetNoteEl = document.getElementById("datasetNote");
  const popupAddressById = new Map();
  let popupAddressId = 0;

  function getStationKey(lng, lat) {
    return `${lng.toFixed(6)}|${lat.toFixed(6)}`;
  }

  function getInfoFromStation(station, dataPayload) {
    const props = station?.properties || {};
    const prices = props.Prices || props.prix || {};
    const regularPrice = pickPrice(prices, ["regulier", "regular"]);
    const superPrice = pickPrice(prices, ["super", "premium"]);
    const dieselPrice = pickPrice(prices, ["diesel"]);

    return {
      name: props.Name || props.nom || props.NOM || props.nom_commercial,
      brand: props.brand || props.marque || props.Brand || props.banniere,
      address: props.Address || props.adresse || props.ADRESSE,
      city: props.Region || props.ville || props.City || props.Municipalite || props.MUNICIPALITE,
      postal_code: props.PostalCode || props.codepostal || props.CODE_POSTAL || props.code_postal || "N/A",
      prices: {
        regulier: regularPrice,
        super: superPrice,
        diesel: dieselPrice,
      },
      regularPrice,
      superPrice,
      dieselPrice,
      updated_at:
        prices?.updated_at ||
        prices?.UpdatedAt ||
        props?.Status ||
        props?.maj ||
        dataPayload?.metadata?.generated_at ||
        "N/A",
    };
  }

  function getPriceScales(stations) {
    return {
      regulier: getPriceScale(stations
        .map((station) => {
          const props = station?.properties || {};
          const prices = props.Prices || props.prix || {};
          return pickPrice(prices, ["regulier", "regular"]);
        })
        .filter((price) => price != null)),
      super: getPriceScale(stations
        .map((station) => {
          const props = station?.properties || {};
          const prices = props.Prices || props.prix || {};
          return pickPrice(prices, ["super", "premium"]);
        })
        .filter((price) => price != null)),
      diesel: getPriceScale(stations
        .map((station) => {
          const props = station?.properties || {};
          const prices = props.Prices || props.prix || {};
          return pickPrice(prices, ["diesel"]);
        })
        .filter((price) => price != null)),
    };
  }

  function updateDatasetNote(stations) {
    const hasFuelPriceData = stations.some((station) => {
      const props = station?.properties || {};
      const prices = props.Prices || props.prix || {};
      return pickPrice(prices, ["regulier", "regular"]) != null ||
        pickPrice(prices, ["super", "premium"]) != null ||
        pickPrice(prices, ["diesel"]) != null;
    });

    if (hasFuelPriceData) {
      datasetNoteEl.style.display = "none";
      datasetNoteEl.textContent = "";
    } else {
      datasetNoteEl.style.display = "block";
      datasetNoteEl.textContent = "Live feed loaded, but no usable price fields were found.";
    }
  }

  function buildPopupHtml(info, popupColor, addressId, lat, lng) {
    const hasPrices = info.prices.regulier != null || info.prices.super != null || info.prices.diesel != null;
    const hasUpdatedAt = info.updated_at && info.updated_at !== "N/A";
    const priceItems = [
      info.prices.regulier != null
        ? `<div class="popup-price-item"><span class="popup-price-type">Regular</span><span class="popup-price-value">${formatPrice(info.prices.regulier)}</span></div>`
        : "",
      info.prices.super != null
        ? `<div class="popup-price-item"><span class="popup-price-type">Super</span><span class="popup-price-value">${formatPrice(info.prices.super)}</span></div>`
        : "",
      info.prices.diesel != null
        ? `<div class="popup-price-item"><span class="popup-price-type">Diesel</span><span class="popup-price-value">${formatPrice(info.prices.diesel)}</span></div>`
        : "",
    ].filter(Boolean).join("");
    const priceStatusHtml = hasPrices
      ? `<div class="popup-prices">${priceItems}</div>`
      : '<div class="popup-empty">No gas price available yet for this station.</div>';
    const fullAddress = [info.address, info.city, info.postal_code]
      .filter((part) => part && part !== "N/A")
      .join(", ");
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;
    popupAddressById.set(addressId, fullAddress || "N/A");

    return `
      <div class="popup-card">
        <div class="popup-title-row">
          <div class="popup-title">${info.name || "Unknown Name"}</div>
          <span class="popup-status-dot" style="background:${popupColor};"></span>
        </div>
        <div class="popup-subtitle">${info.brand || "Independent station"}</div>
        <div class="popup-grid">
          <div class="popup-row">
            <div class="popup-label">Address</div>
            <div class="popup-address-line">
              <div class="popup-value">${info.address || "N/A"}, ${info.city || "N/A"}</div>
              <div class="popup-actions">
                <button class="popup-copy-btn" type="button" data-copy-address-id="${addressId}">Copy</button>
                <a class="popup-route-btn" href="${directionsUrl}" target="_blank" rel="noopener noreferrer">Go</a>
              </div>
            </div>
          </div>
          <div class="popup-row">
            <div class="popup-label">Prices</div>
            ${priceStatusHtml}
          </div>
          <div class="popup-row">
            <div class="popup-label">Updated</div>
            <div class="popup-value">${hasUpdatedAt ? info.updated_at : "Not updated"}</div>
          </div>
        </div>
      </div>
    `;
  }

  if (activeMap) {
    activeMap.remove();
    activeMap = null;
  }

  mapContainer.classList.remove("has-best-station");

  async function copyText(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  try {
    const data = await fetchStationsJson(url);
    const stations = Array.isArray(data?.features) ? data.features : [];

    if (stations.length === 0) {
      throw new Error("No station records were returned by the data source");
    }

    updateDatasetNote(stations);

    console.log(`Map loaded. Zoom to ${MARKER_MIN_ZOOM}+ to show station markers.`);
    console.log(`Total entries: ${stations.length}`);

    let priceScales = getPriceScales(stations);

    const map = new maplibregl.Map({
      container: mapContainer,
      style: CARTO_VOYAGER_STYLE,
      center: [-71.208, 46.8139],
      zoom: 5.6,
      pitch: 20,
    });
    activeMap = map;

    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();

    mapContainer.addEventListener("click", async (event) => {
      const copyButton = event.target.closest("[data-copy-address-id]");
      if (!copyButton) {
        return;
      }

      const addressId = copyButton.getAttribute("data-copy-address-id");
      const addressText = popupAddressById.get(addressId);
      if (!addressText) {
        return;
      }

      try {
        await copyText(addressText);
        const originalText = copyButton.textContent;
        copyButton.textContent = "Copied";
        copyButton.classList.add("is-copied");
        window.setTimeout(() => {
          copyButton.textContent = originalText;
          copyButton.classList.remove("is-copied");
        }, 1200);
      } catch {
        alert("Unable to copy the address on this device.");
      }
    });

    const locateBtn = document.getElementById("locateBtn");
    const bestStationBtn = document.getElementById("bestStationBtn");
    const colorFilterBtn = document.getElementById("colorFilterBtn");
    const colorFilterPanel = document.getElementById("colorFilterPanel");
    const markerModeBtn = document.getElementById("markerModeBtn");
    markerModeBtn.hidden = true;

    // About popup
    const appTitle = document.getElementById("appTitle");
    const aboutOverlay = document.getElementById("aboutOverlay");
    const aboutCloseBtn = document.getElementById("aboutCloseBtn");
    function openAbout() { aboutOverlay.classList.add("is-open"); }
    function closeAbout() { aboutOverlay.classList.remove("is-open"); }
    function isAboutOpen() { return aboutOverlay.classList.contains("is-open"); }
    appTitle.addEventListener("click", openAbout);
    appTitle.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAbout(); } });
    aboutCloseBtn.addEventListener("click", closeAbout);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && isAboutOpen()) closeAbout(); });
    aboutOverlay.addEventListener("click", (e) => { if (e.target === aboutOverlay) closeAbout(); });
    
    let userLocationMarker = null;
    let userLocation = null;
    let markerRenderMode = "dom";
    let webglMarkerPopup = null;
    let selectedFuelType = "regular";
    const selectedColors = new Set(PRICE_LEVEL_COLORS);
    const stationCandidates = [];
    const stationCandidatesByKey = new Map();

    function openWebglMarkerPopup(candidate) {
      if (!candidate?.popupHtml) {
        return;
      }

      if (webglMarkerPopup) {
        webglMarkerPopup.remove();
      }

      webglMarkerPopup = new maplibregl.Popup({ offset: 24 })
        .setLngLat([candidate.lng, candidate.lat])
        .setHTML(candidate.popupHtml)
        .addTo(map);
    }

    function openCandidatePopup(candidate) {
      if (!candidate) {
        return;
      }

      if (markerRenderMode === "webgl") {
        openWebglMarkerPopup(candidate);
      } else {
        candidate.marker.togglePopup();
      }
    }

    function resetLocateButton() {
      locateBtn.classList.remove("is-loading");
      locateBtn.disabled = false;
    }

    function getActiveFuelOption() {
      return FUEL_TYPE_OPTIONS.find((option) => option.id === selectedFuelType) || FUEL_TYPE_OPTIONS[0];
    }

    function getCandidateActivePrice(candidate) {
      const option = getActiveFuelOption();
      return candidate.prices[option.priceKey] ?? null;
    }

    function getCandidateColor(candidate) {
      const option = getActiveFuelOption();
      return getPriceLevelColor(getCandidateActivePrice(candidate), priceScales[option.priceKey]);
    }

    function getFilteredCandidates() {
      return stationCandidates.filter((candidate) => {
        const activePrice = getCandidateActivePrice(candidate);
        const activeColor = getCandidateColor(candidate);
        return activePrice != null && activeColor != null && selectedColors.has(activeColor);
      });
    }

    function findBestStation() {
      if (!userLocation) {
        return null;
      }

      const candidates = getFilteredCandidates().filter((candidate) => candidate.regularPrice != null);
      if (candidates.length === 0) {
        return null;
      }

      const enriched = candidates.map((candidate) => ({
        ...candidate,
        distanceKm: haversineDistanceKm(userLocation, { lat: candidate.lat, lng: candidate.lng }),
        colorRank: getPriceLevelRank(getCandidateColor(candidate)),
      }));
      const sortedByDistance = enriched.sort((left, right) => left.distanceKm - right.distanceKm);
      const closestFive = sortedByDistance.slice(0, Math.min(5, sortedByDistance.length));
      const averageColorRank = closestFive.reduce((sum, candidate) => sum + candidate.colorRank, 0) / closestFive.length;

      const betterThanLocalAverage = sortedByDistance.find((candidate) => candidate.colorRank > averageColorRank);
      if (betterThanLocalAverage) {
        return betterThanLocalAverage;
      }

      return closestFive.reduce((best, candidate) => {
        if (!best) {
          return candidate;
        }

        if (candidate.colorRank > best.colorRank) {
          return candidate;
        }

        if (candidate.colorRank === best.colorRank && candidate.distanceKm < best.distanceKm) {
          return candidate;
        }

        return best;
      }, null);
    }

    function updateBestStationButtonState() {
      if (!userLocation) {
        bestStationBtn.hidden = true;
        mapContainer.classList.remove("has-best-station");
        return;
      }

      bestStationBtn.hidden = false;
      mapContainer.classList.add("has-best-station");
      bestStationBtn.disabled = getFilteredCandidates().length === 0;
    }

    locateBtn.addEventListener("click", () => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser.");
        return;
      }

      if (!window.isSecureContext) {
        alert("Location requires HTTPS on iPhone Safari. Open this app from a secure URL, not a local HTTP address.");
        return;
      }

      locateBtn.disabled = true;
      locateBtn.classList.add("is-loading");

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { longitude, latitude } = pos.coords;

          if (userLocationMarker) {
            userLocationMarker.remove();
          }

          const el = document.createElement("div");
          el.className = "user-location-marker";

          userLocationMarker = new maplibregl.Marker({ element: el })
            .setLngLat([longitude, latitude])
            .setPopup(new maplibregl.Popup({ offset: 14 }).setHTML("<strong>You are here</strong>"))
            .addTo(map);

          userLocation = { lat: latitude, lng: longitude };

          map.flyTo({ center: [longitude, latitude], zoom: 13, duration: 1400 });

          updateBestStationButtonState();
          resetLocateButton();
        },
        (error) => {
          if (error?.code === error.PERMISSION_DENIED) {
            alert("Location permission was denied. Enable location access for Safari and try again.");
          } else if (error?.code === error.TIMEOUT) {
            alert("Location request timed out. Try again with a stronger GPS or network signal.");
          } else {
            alert("Unable to retrieve your location. On iPhone, make sure the page is opened over HTTPS and Safari location access is allowed.");
          }

          resetLocateButton();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });

    const markers = [];
    let markersVisible = null;
    let lastWebglDeclutterZoomBucket = null;

    bestStationBtn.addEventListener("click", () => {
      const bestStation = findBestStation();
      if (!bestStation) {
        alert("No visible gas station with a regular price is available right now.");
        return;
      }

      openCandidatePopup(bestStation);
      map.flyTo({ center: [bestStation.lng, bestStation.lat], zoom: 14, duration: 1400 });
    });

    function refreshOverlaySource() {
      const source = map.getSource(PRICE_OVERLAY_SOURCE_ID);
      if (!source) {
        return;
      }

      source.setData({
        type: "FeatureCollection",
        features: stationCandidates.map((candidate) => {
          const activePrice = getCandidateActivePrice(candidate);
          const activeColor = getCandidateColor(candidate);
          if (activePrice == null || activeColor == null || !selectedColors.has(activeColor)) {
            return null;
          }

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [candidate.lng, candidate.lat],
            },
            properties: {
              color: activeColor,
            },
          };
        }).filter(Boolean),
      });
    }

    function refreshWebglMarkerSource() {
      const source = map.getSource(WEBGL_MARKER_SOURCE_ID);
      if (!source) {
        return;
      }

      const cellSizePx = 34;
      const acceptedByCell = new Map();
      const features = stationCandidates
        .map((candidate) => {
          const activePrice = getCandidateActivePrice(candidate);
          const activeColor = getCandidateColor(candidate);
          if (activePrice == null || activeColor == null || !selectedColors.has(activeColor)) {
            return null;
          }

          return {
            candidate,
            sortKey: Math.round(activePrice * 1000),
            color: activeColor,
            priceText: formatPriceShort(activePrice),
          };
        })
        .filter(Boolean)
        .sort((left, right) => right.sortKey - left.sortKey)
        .filter((entry) => {
          const point = map.project([entry.candidate.lng, entry.candidate.lat]);
          const cellX = Math.floor(point.x / cellSizePx);
          const cellY = Math.floor(point.y / cellSizePx);

          for (let x = cellX - 1; x <= cellX + 1; x += 1) {
            for (let y = cellY - 1; y <= cellY + 1; y += 1) {
              if (acceptedByCell.has(`${x}|${y}`)) {
                return false;
              }
            }
          }

          acceptedByCell.set(`${cellX}|${cellY}`, true);
          return true;
        })
        .map((entry) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [entry.candidate.lng, entry.candidate.lat],
          },
          properties: {
            key: entry.candidate.key,
            color: entry.color,
            priceText: entry.priceText,
            pillImageId: `${WEBGL_MARKER_PILL_IMAGE_PREFIX}-${colorToImageKey(entry.color)}`,
            sortKey: entry.sortKey,
          },
        }));

      source.setData({
        type: "FeatureCollection",
        features,
      });
    }

    function setWebglMarkerLayerVisibility(show) {
      const visibility = show ? "visible" : "none";
      if (map.getLayer(WEBGL_MARKER_BG_LAYER_ID)) {
        map.setLayoutProperty(WEBGL_MARKER_BG_LAYER_ID, "visibility", visibility);
      }
      if (map.getLayer(WEBGL_MARKER_TEXT_LAYER_ID)) {
        map.setLayoutProperty(WEBGL_MARKER_TEXT_LAYER_ID, "visibility", visibility);
      }
    }

    function setMarkerRenderMode(mode) {
      markerRenderMode = mode === "webgl" ? "webgl" : "dom";
      markerModeBtn.textContent = markerRenderMode === "webgl" ? "WebGL" : "DOM";
      markerModeBtn.setAttribute("aria-label", `Switch marker renderer (current: ${markerRenderMode})`);
      markerModeBtn.title = `Marker mode: ${markerRenderMode.toUpperCase()}`;
      if (markerRenderMode !== "webgl" && webglMarkerPopup) {
        webglMarkerPopup.remove();
        webglMarkerPopup = null;
      }
      updateMarkerVisibility(true);
    }

    function updateMarkerVisibility(force = false) {
      const shouldShow = map.getZoom() >= MARKER_MIN_ZOOM;
      const zoomBucket = Math.round(map.getZoom() * 4);
      const shouldRefreshWebgl = markerRenderMode === "webgl" && shouldShow && lastWebglDeclutterZoomBucket !== zoomBucket;
      if (!force && markersVisible === shouldShow && !shouldRefreshWebgl) {
        return;
      }
      markersVisible = shouldShow;
      if (markerRenderMode === "webgl" && shouldShow) {
        lastWebglDeclutterZoomBucket = zoomBucket;
      }

      const showDomMarkers = markerRenderMode === "dom" && shouldShow;

      markers.forEach((entry) => {
        const activePrice = getCandidateActivePrice(entry);
        const activeColor = getCandidateColor(entry);
        const fuelLabel = getActiveFuelOption().label;

        updatePriceMarkerElement(entry.marker.getElement(), activePrice, activeColor, fuelLabel);

        const matchesColor = activeColor != null && selectedColors.has(activeColor);
        entry.marker.getElement().style.display = showDomMarkers && activePrice != null && matchesColor ? "" : "none";
      });

      refreshOverlaySource();
      refreshWebglMarkerSource();
      setWebglMarkerLayerVisibility(markerRenderMode === "webgl" && shouldShow);
      updateBestStationButtonState();
    }

    function renderColorFilterOptions() {
      colorFilterPanel.innerHTML = "";

      const fuelSection = document.createElement("div");
      fuelSection.className = "filter-section";

      const fuelTitle = document.createElement("div");
      fuelTitle.className = "filter-section-title";
      fuelTitle.textContent = "Fuel";

      const fuelList = document.createElement("div");
      fuelList.className = "fuel-filter-list";

      FUEL_TYPE_OPTIONS.forEach((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "fuel-filter-btn";
        button.textContent = option.label;
        button.classList.toggle("is-active", selectedFuelType === option.id);
        button.addEventListener("click", () => {
          selectedFuelType = selectedFuelType === option.id ? null : option.id;
          renderColorFilterOptions();
          updateMarkerVisibility(true);
        });
        fuelList.append(button);
      });

      fuelSection.append(fuelTitle, fuelList);
      colorFilterPanel.append(fuelSection);

      const colorSection = document.createElement("div");
      colorSection.className = "filter-section";

      const colorTitle = document.createElement("div");
      colorTitle.className = "filter-section-title";
      colorTitle.textContent = "Price";

      const colorList = document.createElement("div");
      colorList.className = "color-filter-list";

      PRICE_LEVEL_COLORS.forEach((color) => {
        const option = document.createElement("label");
        option.className = "color-filter-option";
        option.title = getPriceLevelLabel(color);

        const input = document.createElement("input");
        input.type = "checkbox";
        input.checked = selectedColors.has(color);
        input.setAttribute("aria-label", getPriceLevelLabel(color));
        input.addEventListener("change", () => {
          if (input.checked) {
            selectedColors.add(color);
          } else if (selectedColors.size > 1) {
            selectedColors.delete(color);
          } else {
            input.checked = true;
          }

          updateMarkerVisibility(true);
        });

        const swatch = document.createElement("span");
        swatch.className = "color-filter-swatch";
        swatch.style.backgroundColor = color;

        option.append(input, swatch);
        colorList.append(option);
      });

      colorSection.append(colorTitle, colorList);
      colorFilterPanel.append(colorSection);
    }

    colorFilterBtn.addEventListener("click", () => {
      colorFilterPanel.hidden = !colorFilterPanel.hidden;
    });

    markerModeBtn.addEventListener("click", () => {
      setMarkerRenderMode(markerRenderMode === "dom" ? "webgl" : "dom");
    });

    map.getContainer().addEventListener("click", (event) => {
      if (!colorFilterPanel.hidden && !colorFilterPanel.contains(event.target) && event.target !== colorFilterBtn && !colorFilterBtn.contains(event.target)) {
        colorFilterPanel.hidden = true;
      }
    });

    function handleWebglMarkerClick(event) {
      if (markerRenderMode !== "webgl") {
        return;
      }

      const feature = event?.features?.[0];
      const key = feature?.properties?.key;
      if (!key) {
        return;
      }

      const candidate = stationCandidatesByKey.get(key);
      openCandidatePopup(candidate);
    }

    renderColorFilterOptions();

    map.on("zoom", () => updateMarkerVisibility());

    const bounds = new maplibregl.LngLatBounds();

    stations.forEach((station) => {
      const props = station?.properties || {};
      const prices = props.Prices || props.prix || {};
      const coordinates = station?.geometry?.coordinates;
      const lng = Number(coordinates?.[0]);
      const lat = Number(coordinates?.[1]);

      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return;
      }

      const regularPrice = pickPrice(prices, ["regulier", "regular"]);
      const superPrice = pickPrice(prices, ["super", "premium"]);
      const dieselPrice = pickPrice(prices, ["diesel"]);

      const addressId = String(popupAddressId++);
      const popupColor = getPriceLevelColor(regularPrice, priceScales.regulier) || "#6b7280";
      const info = getInfoFromStation(station, data);
      const popupHtml = buildPopupHtml(info, popupColor, addressId, lat, lng);

      const markerColor = getPriceLevelColor(regularPrice, priceScales.regulier);
      const marker = new maplibregl.Marker({ element: createPriceMarkerElement(regularPrice, "Regular") })
        .setLngLat([lng, lat])
        .setPopup(new maplibregl.Popup({ offset: 24 }).setHTML(popupHtml))
        .addTo(map);

      updatePriceMarkerElement(marker.getElement(), regularPrice, markerColor, "Regular");

      const key = getStationKey(lng, lat);

      markers.push({ marker, key, prices: { regulier: regularPrice, super: superPrice, diesel: dieselPrice }, lat, lng, addressId });
      stationCandidates.push({
        marker,
        key,
        addressId,
        popupHtml,
        prices: { regulier: regularPrice, super: superPrice, diesel: dieselPrice },
        regularPrice,
        lat,
        lng,
      });
      stationCandidatesByKey.set(key, stationCandidates[stationCandidates.length - 1]);

      bounds.extend([lng, lat]);
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 40, maxZoom: 13, duration: 0 });
    }

    map.on("load", () => {
      addWebglMarkerImages(map);

      map.addSource(PRICE_OVERLAY_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addSource(WEBGL_MARKER_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: PRICE_OVERLAY_GLOW_LAYER_ID,
        type: "circle",
        source: PRICE_OVERLAY_SOURCE_ID,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 10, 10, 20, 14, 34],
          "circle-color": ["get", "color"],
          "circle-opacity": 0.2,
          "circle-blur": 0.9,
        },
        layout: {
          visibility: "visible",
        },
      });

      map.addLayer({
        id: PRICE_OVERLAY_CORE_LAYER_ID,
        type: "circle",
        source: PRICE_OVERLAY_SOURCE_ID,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 3, 10, 6, 14, 9],
          "circle-color": ["get", "color"],
          "circle-opacity": 0.65,
          "circle-stroke-width": 0,
        },
        layout: {
          visibility: "visible",
        },
      });

      map.addLayer({
        id: WEBGL_MARKER_BG_LAYER_ID,
        type: "symbol",
        source: WEBGL_MARKER_SOURCE_ID,
        minzoom: MARKER_MIN_ZOOM,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": ["get", "pillImageId"],
          "icon-anchor": "center",
          "icon-size": ["interpolate", ["linear"], ["zoom"], 12, 1.04, 14, 1.14],
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "icon-text-fit": "both",
          "icon-text-fit-padding": [6, 8, 6, 8],
          "text-field": ["get", "priceText"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 12, 12, 14, 14],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          "symbol-sort-key": ["get", "sortKey"],
          visibility: "none",
        },
        paint: {
          "icon-opacity": 1,
          "text-color": "#ffffff",
          "text-halo-color": "rgba(15, 23, 42, 0)",
          "text-halo-width": 0,
        },
      });

      map.on("click", WEBGL_MARKER_BG_LAYER_ID, handleWebglMarkerClick);

      map.on("mouseenter", WEBGL_MARKER_BG_LAYER_ID, () => {
        if (markerRenderMode === "webgl") {
          map.getCanvas().style.cursor = "pointer";
        }
      });
      map.on("mouseleave", WEBGL_MARKER_BG_LAYER_ID, () => {
        map.getCanvas().style.cursor = "";
      });

      refreshOverlaySource();
      refreshWebglMarkerSource();
      setMarkerRenderMode("webgl");
    });

    updateMarkerVisibility(true);

    refreshPricesInPlace = async () => {
      const refreshedData = await fetchStationsJson(url);
      const refreshedStations = Array.isArray(refreshedData?.features) ? refreshedData.features : [];
      if (refreshedStations.length === 0) {
        throw new Error("No station records were returned by the data source");
      }

      updateDatasetNote(refreshedStations);
      priceScales = getPriceScales(refreshedStations);

      const refreshedByKey = new Map();
      refreshedStations.forEach((station) => {
        const coordinates = station?.geometry?.coordinates;
        const lng = Number(coordinates?.[0]);
        const lat = Number(coordinates?.[1]);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
          return;
        }

        const key = getStationKey(lng, lat);
        const info = getInfoFromStation(station, refreshedData);
        refreshedByKey.set(key, { lng, lat, info });
      });

      markers.forEach((entry) => {
        const refreshed = refreshedByKey.get(entry.key);
        if (!refreshed) {
          return;
        }

        entry.prices.regulier = refreshed.info.regularPrice;
        entry.prices.super = refreshed.info.superPrice;
        entry.prices.diesel = refreshed.info.dieselPrice;
      });

      stationCandidates.forEach((candidate) => {
        const refreshed = refreshedByKey.get(candidate.key);
        if (!refreshed) {
          return;
        }

        candidate.prices.regulier = refreshed.info.regularPrice;
        candidate.prices.super = refreshed.info.superPrice;
        candidate.prices.diesel = refreshed.info.dieselPrice;
        candidate.regularPrice = refreshed.info.regularPrice;

        const popupColor = getPriceLevelColor(refreshed.info.regularPrice, priceScales.regulier) || "#6b7280";
        const popupHtml = buildPopupHtml(refreshed.info, popupColor, candidate.addressId, candidate.lat, candidate.lng);
        candidate.popupHtml = popupHtml;
        candidate.marker.setPopup(new maplibregl.Popup({ offset: 24 }).setHTML(popupHtml));
      });

      updateMarkerVisibility(true);
    };
  } catch (err) {
    console.error("Error fetching gas data:", err);
    datasetNoteEl.style.display = "none";
    datasetNoteEl.textContent = "";
    console.error("Error loading station data. The upstream source may be unavailable.");
  }
}

loadGasStations();

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

setInterval(async () => {
  try {
    if (typeof refreshPricesInPlace === "function") {
      await refreshPricesInPlace();
      console.log(`Last updated at: ${new Date().toLocaleTimeString()}`);
    }
  } catch (err) {
    console.error("Auto-refresh failed:", err);
  }
}, REFRESH_INTERVAL_MS);
